import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AppShell, SCROLLER_ID } from './components/AppShell';
import { FEED_ROUTE_ID } from './lib/feed-route';
import { PostCard } from './components/PostCard';
import { Failed, Loading } from './components/states';
import { getRouteCanaryFeed } from './lib/api';
import { trackHistoryTrail } from './lib/leaving';
import { ThreadSheetProvider } from './lib/thread-sheet';
import type { ComposeSearch } from './screens/ComposeScreen';
import { FeedScreen } from './screens/FeedScreen';

/*
 * THE FEED IS THE ONLY EAGER SCREEN. It is the signed-in front door, while every
 * other screen is reached by a tap that gives the router time to preload its
 * chunk. Keeping compose, profile controls and the 3D dove out of the entry bundle
 * materially shortens parse/evaluation before the first feed paint.
 */
const ComposeScreen = lazyRouteComponent(
  () => import('./screens/ComposeScreen'),
  'ComposeScreen',
);
const CorrespondenceScreen = lazyRouteComponent(
  () => import('./screens/CorrespondenceScreen'),
  'CorrespondenceScreen',
);
const DoveScreen = lazyRouteComponent(() => import('./screens/DoveScreen'), 'DoveScreen');
const JoinScreen = lazyRouteComponent(() => import('./screens/JoinScreen'), 'JoinScreen');
const LetterScreen = lazyRouteComponent(() => import('./screens/LetterScreen'), 'LetterScreen');
const LetterCommentsSheet = lazyRouteComponent(
  () => import('./screens/LetterScreen'),
  'LetterCommentsSheet',
);
const LoftScreen = lazyRouteComponent(() => import('./screens/LoftScreen'), 'LoftScreen');
const NotificationsScreen = lazyRouteComponent(
  () => import('./screens/NotificationsScreen'),
  'NotificationsScreen',
);
const EditProfileScreen = lazyRouteComponent(
  () => import('./screens/EditProfileScreen'),
  'EditProfileScreen',
);
const PeopleScreen = lazyRouteComponent(() => import('./screens/PeopleScreen'), 'PeopleScreen');
const ProfileScreen = lazyRouteComponent(() => import('./screens/ProfileScreen'), 'ProfileScreen');
const SpaceScreen = lazyRouteComponent(() => import('./screens/SpaceScreen'), 'SpaceScreen');

/*
 * Four tabs, matching docs/1 § Surfaces exactly: Feed, Loft, Dove, You — plus
 * the writing and profile routes that hang off them. All four are now built;
 * the loft's placeholder is gone.
 *
 * To add a screen: write the component, `createRoute` it against `rootRoute`,
 * and list it in `addChildren`. A full-screen one also goes in FULL_SCREEN in
 * AppShell, or the tab bar will sit under it.
 *
 * Still missing from the native integration (docs/3): Android hardware back
 * wired to router history, `appUrlOpen` deep links, a `notFoundRoute` and an
 * error boundary. `createBrowserHistory` is correct under Capacitor. Do NOT
 * switch to memory history, which breaks deep links.
 */
const rootRoute = createRootRoute({ component: AppShell });

/*
 * A CONVERSATION OPENED FROM A LIST KEEPS THE LIST UNDER IT.
 *
 * Each list route owns one private child route whose only rendering is the
 * comments sheet. Navigation masks that implementation URL as `/letters/$id`,
 * so copying the address produces the same durable link as the full page. The
 * mask is discarded on reload: without a live list to preserve, the public
 * letter route below is the honest destination.
 *
 * These children are repeated for Feed, a room and both profile addresses
 * because nesting is what keeps each actual parent component mounted—and its
 * scroll position intact. A root-level modal route would replace the very
 * surface the drawer is meant to preserve.
 */
const feedRoute = createRoute({
  getParentRoute: () => rootRoute,
  // An index route (`path: '/'`) cannot match child paths. Keep the feed in
  // a pathless layout so its comments drawer can match and preserve the list.
  // The id is shared with the tab bar, which has no path to compare against
  // once this route is pathless — see `FEED_ROUTE_ID`.
  id: FEED_ROUTE_ID,
  component: function FeedRoute() {
    const navigate = useNavigate();
    const open = useCallback(
      (id: string) => {
        void navigate({
          to: '/comments/$id/sheet',
          params: { id },
          mask: {
            to: '/letters/$id',
            params: { id },
            unmaskOnReload: true,
          },
          resetScroll: false,
        });
      },
      [navigate],
    );

    return (
      <ThreadSheetProvider open={open}>
        <FeedScreen />
        <Outlet />
      </ThreadSheetProvider>
    );
  },
});

const feedIndexRoute = createRoute({
  getParentRoute: () => feedRoute,
  path: '/',
});

const feedCommentsRoute = createRoute({
  getParentRoute: () => feedRoute,
  path: 'comments/$id/sheet',
  component: function FeedCommentsRoute() {
    const { id } = feedCommentsRoute.useParams();
    return <LetterCommentsSheet id={id} />;
  },
});

/*
 * A production route check with no account and no user content. It renders the
 * real card, uses the real sheet navigation, and reads the real GET /posts/:id
 * contract; only the fixed synthetic payload is public.
 */
const routeCanaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/_canary',
  component: function RouteCanaryRoute() {
    const navigate = useNavigate();
    const feed = useQuery({
      queryKey: ['route-canary-feed'],
      queryFn: getRouteCanaryFeed,
      retry: false,
    });
    const open = useCallback(
      (id: string) => {
        void navigate({
          to: '/_canary/comments/$id/sheet',
          params: { id },
          resetScroll: false,
        });
      },
      [navigate],
    );

    if (feed.isPending) return <Loading />;
    if (feed.isError) return <Failed onRetry={() => void feed.refetch()} />;

    return (
      <ThreadSheetProvider open={open}>
        <div data-testid="route-canary-feed">
          {feed.data.groups.flatMap((group) =>
            group.posts.map((post) => (
              <PostCard key={post.id} post={post} commentAction="open" />
            )),
          )}
        </div>
        <Outlet />
      </ThreadSheetProvider>
    );
  },
});

const routeCanaryCommentsRoute = createRoute({
  getParentRoute: () => routeCanaryRoute,
  path: 'comments/$id/sheet',
  component: function RouteCanaryCommentsRoute() {
    const { id } = routeCanaryCommentsRoute.useParams();
    return <LetterCommentsSheet id={id} />;
  },
});

const loftRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loft',
  component: LoftScreen,
});

/*
 * One room (docs/5 § D260803-1933). Reached from the search field at the top of the feed,
 * which is the only entrance: there is no directory screen listing every room, and
 * a `/spaces` index would be one.
 *
 * `$slug` RATHER THAN AN ID, because a room's name is the thing people would type
 * or paste, and `/spaces/slow-cooking` survives a database that renumbers. The slug
 * is already on `PublicSpace` for exactly this (docs/4).
 *
 * NOT NESTED UNDER `/` and not a search parameter on the feed. A room is a place
 * you go rather than a state the feed is in — the hardware back button and the
 * header's arrow both have somewhere to return to — and `?space=` on the feed would
 * make every room's address a variant of one screen that then had to decide which
 * of the two things it was.
 */
const spaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spaces/$slug',
  component: function SpaceRoute() {
    const { slug } = spaceRoute.useParams();
    const navigate = useNavigate();
    const open = useCallback(
      (id: string) => {
        void navigate({
          to: '/spaces/$slug/comments/$id/sheet',
          params: { slug, id },
          mask: {
            to: '/letters/$id',
            params: { id },
            unmaskOnReload: true,
          },
          resetScroll: false,
        });
      },
      [navigate, slug],
    );

    return (
      <ThreadSheetProvider open={open}>
        <SpaceScreen slug={slug} />
        <Outlet />
      </ThreadSheetProvider>
    );
  },
});

const spaceCommentsRoute = createRoute({
  getParentRoute: () => spaceRoute,
  path: 'comments/$id/sheet',
  component: function SpaceCommentsRoute() {
    const { id } = spaceCommentsRoute.useParams();
    return <LetterCommentsSheet id={id} />;
  },
});

/*
 * `/dove` rather than `/me/dove`: the bird is not a section of your profile, and
 * a nested path would make it one. It is also deliberately NOT `/loft`, because
 * the loft is where letters live, and putting a wardrobe in the same room as
 * someone's correspondence is how a keepsake starts reading as a game
 * (docs/5 D260731-1551).
 */
const doveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dove',
  component: DoveScreen,
});

/*
 * THERE IS NO `?tab=` ON THE CARD ROUTES ANY MORE, and the validator that
 * narrowed it is gone with it (docs/5 § D260802-2337). A profile is one column now — who
 * this is, then everything they have written — so there is no tab for a link to
 * select and nothing about where you are on somebody's card left to carry in a
 * URL. The one destination the parameter was really for, a pair's letters, is a
 * route of its own and always was: `/loft/$handle`, below.
 *
 * A link still in the wild arrives with an unvalidated `?tab=` in the root's
 * search and nothing reads it, which is the same silence `?tab=media` has met
 * since § D260802-2203 removed that tab.
 */

/*
 * `/me` and `/u/:handle` are the SAME COMPONENT, because docs/1 gives a person
 * exactly one projection and your own is not a second kind of object. What
 * differs is the viewer, and the screen resolves that itself.
 */
const youRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: function YouRoute() {
    const navigate = useNavigate();
    const open = useCallback(
      (id: string) => {
        void navigate({
          to: '/me/comments/$id/sheet',
          params: { id },
          mask: {
            to: '/letters/$id',
            params: { id },
            unmaskOnReload: true,
          },
          resetScroll: false,
        });
      },
      [navigate],
    );

    return (
      <ThreadSheetProvider open={open}>
        <ProfileScreen />
        <Outlet />
      </ThreadSheetProvider>
    );
  },
});

const youCommentsRoute = createRoute({
  getParentRoute: () => youRoute,
  path: 'comments/$id/sheet',
  component: function YouCommentsRoute() {
    const { id } = youCommentsRoute.useParams();
    return <LetterCommentsSheet id={id} />;
  },
});

/*
 * Your followers and the people you follow.
 *
 * NESTED UNDER `/me`, unlike `/notifications` and `/dove`, and the difference is
 * whose object it is. Notifications are the public layer answering you and the
 * dove is a companion rather than a section of a profile; these two lists ARE
 * your profile's own graph, and there is deliberately no `/u/$handle/people`
 * counterpart — see PeopleScreen on why a stranger's follower list is the
 * browsing surface docs/5 § D260730-2028 removed.
 */
const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me/people',
  component: PeopleScreen,
});

/*
 * Changing your own card (docs/5 § D260805-0242).
 *
 * A PLACE RATHER THAN A SHEET, and the reason is the keyboard. Six fields and a
 * camera control inside a bottom sheet leaves the field being typed into behind the
 * keyboard on a small phone, with the save button under it; a screen scrolls. It is
 * also somewhere the back gesture means what it looks like it means, which a sheet
 * over a profile does not.
 *
 * NESTED UNDER `/me` FOR THE SAME REASON `/me/people` IS: this is your own card's
 * own surface, and there is deliberately no `/u/$handle/edit` counterpart.
 */
const editProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me/edit',
  component: EditProfileScreen,
});

/*
 * Notifications, a page rather than a bell in a header that opened nothing
 * (docs/5 § D260801-1640).
 *
 * TOP LEVEL, NOT `/me/notifications`, and the reason is the same one that keeps
 * `/dove` off `/me`: what lands here is the public layer answering you — replies,
 * follows, a post that travelled — and none of that is a section of your profile.
 * It reads as an inbox for the open half of the product, which is what it is.
 *
 * Reached from the You tab because that is where the app's own switches live
 * (docs/5 § D260801-1053 removed the app bar that used to hold them). It carries no tab of
 * its own: a fifth tab for a surface visited once a day would spend permanent
 * screen furniture on it.
 */
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationsScreen,
});

/*
 * The social card. A public, deep-linkable URL, and the same one must render on
 * the web when a Universal Link falls through on a device without the app
 * (docs/8 § G), which is the social side's growth loop.
 *
 * It is login-walled and `noindex` in the product: user content is never
 * indexable and OG previews never carry user photos (docs/2 § Indexability).
 */
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/u/$handle',
  component: function ProfileRoute() {
    const { handle } = profileRoute.useParams();
    const navigate = useNavigate();
    const open = useCallback(
      (id: string) => {
        void navigate({
          to: '/u/$handle/comments/$id/sheet',
          params: { handle, id },
          mask: {
            to: '/letters/$id',
            params: { id },
            unmaskOnReload: true,
          },
          resetScroll: false,
        });
      },
      [handle, navigate],
    );

    return (
      <ThreadSheetProvider open={open}>
        <ProfileScreen handle={handle} />
        <Outlet />
      </ThreadSheetProvider>
    );
  },
});

const profileCommentsRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: 'comments/$id/sheet',
  component: function ProfileCommentsRoute() {
    const { id } = profileCommentsRoute.useParams();
    return <LetterCommentsSheet id={id} />;
  },
});

/*
 * An invitation (docs/5 § D260805-0328).
 *
 * THE ONLY ROUTE IN THE PRODUCT THAT RENDERS WITHOUT AN ACCOUNT, and the login
 * wall knows about it by name — see `Gate` in AppShell, which is the one place
 * that decision can live. Everything else here is walled by default, which is the
 * direction a mistake should fall in.
 *
 * `$code` IS NOT VALIDATED HERE, for the same reason `to` on `/compose` is only
 * narrowed to a string: what a valid code looks like is the server's business, the
 * server has to check it again regardless, and a client-side pattern would mean
 * two definitions of one format. A segment that cannot be a code resolves to
 * nothing and the screen says so in one line.
 *
 * `/join/:code` RATHER THAN `/?invite=CODE`, deliberately. A query parameter on
 * the feed would make the app's front door a variant of a screen that then had to
 * decide which of two things it was — the same argument that keeps a room at
 * `/spaces/$slug` rather than `?space=` (docs/5 § D260803-1933). It is also a URL somebody
 * reads before tapping: a path says *join*, and a query string says nothing.
 */
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$code',
  component: function JoinRoute() {
    const { code } = joinRoute.useParams();
    return <JoinScreen code={code} />;
  },
});

/*
 * Writing is a ROUTE rather than a modal, which buys three things a dialog
 * does not: the Android hardware back button behaves, a draft survives the
 * WebView being killed because the screen owns its own state, and "write" is
 * deep-linkable when a share intent eventually points at it. The tab bar hides
 * while it is open. See AppShell.
 *
 * IT IS THE ONLY WRITING ROUTE NOW. There was a second one at `/loft/new` — "Write
 * a letter", a screen that chose a recipient and could not write to them — and it is
 * gone with the screen (docs/5 § D260803-1551). A post and a letter are tabs on one surface,
 * and `?kind=` is which tab.
 *
 * THE SEARCH IS VALIDATED, WHICH IS THE WHOLE REASON IT IS ALLOWED TO ADDRESS A
 * LETTER. Two of these three parameters name a destination for somebody's writing,
 * and they arrive out of a URL — so anything that is not one of the two literal kinds
 * falls back to a post, and `to` is narrowed to a string here and then resolved
 * against the loft (`LetterComposer` owns that half, and refuses an address that
 * names nowhere). A bad link opens an empty composer; it never opens one pointed at a
 * person who is not there.
 *
 * `on` IS THE OPEN LETTER A COMMENT ANSWERS, and it is the second of the two
 * ADDRESSED entries docs/1 § Surfaces permits to skip the chooser — *Write back*
 * under a direct letter being the first. Narrowed to a string here and resolved
 * against the server by the composer, which refuses an id that names no letter for
 * the same reason `to` is refused: a composer pointed at nothing may not open.
 *
 * `stray` WAS THE THIRD, and it is gone with the object (docs/5 § D260803-1954). It is not
 * validated, so it lands in the root's unvalidated search and nothing reads it —
 * which is the same silence `?tab=media` has met since § D260802-2203, and the reason a
 * removed parameter needs no redirect.
 *
 * Note what is NOT validated into an enum: `to` is a handle, and a handle is not
 * knowable at route-definition time. Narrowing it to `string` is the whole of this
 * layer's job; existence is the composer's.
 */
const composeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compose',
  validateSearch: (search: Record<string, unknown>): ComposeSearch => ({
    /* `on` FORCES THE OPEN-LETTER TAB, because a comment is an open letter and
       cannot be anything else: `?kind=letter&on=<id>` is a link that asks for a
       direct letter addressed to a public conversation, which is not a thing. The
       parameter that names a destination wins over the one that names a tab. */
    kind: search.kind === 'letter' && !search.on ? 'letter' : 'post',
    ...(typeof search.to === 'string' ? { to: search.to } : {}),
    ...(typeof search.on === 'string' ? { on: search.on } : {}),
    /* `at` NAMES NOBODY'S DESTINATION, so narrowing it to a string is the whole
       of the check — see `ComposeSearch.at`. It seeds a mention in the box, and a
       handle that resolves to nobody comes back as text somebody can delete,
       which is exactly what `@` already does inside any other letter. */
    ...(typeof search.at === 'string' ? { at: search.at } : {}),
  }),
  component: function ComposeRoute() {
    /* The route hands the screen its parameters as props, the same shape
       `ProfileRoute` and `CorrespondenceRoute` use below — so the screen is a plain
       component that can be rendered anywhere, and this file stays the only one that
       knows the URL's shape. */
    const search = composeRoute.useSearch();
    return <ComposeScreen {...search} />;
  },
});

/*
 * One correspondence, full screen — the destination the loft's clamped letters
 * point at, and now the only rendering of a correspondence there is: the card's
 * Letters tab was the other one, and it went with the tab strip (docs/5 § D260802-2337).
 * The card links here instead. See CorrespondenceScreen for why it is a route.
 *
 * `$handle` RATHER THAN A LETTER ID, because the object being opened is the
 * correspondence and not one letter in it: a letter is read in the run of the
 * pair's writing, there is at most one a day each, and a per-letter URL would
 * imply a permalink to something that is nobody's to link to.
 *
 * IT IS THE ONLY ROUTE UNDER `/loft` NOW. `/loft/new` used to sit above it as a
 * static segment that had to be matched first, or "new" resolved as somebody's
 * handle; writing moved to `/compose` (§ D260803-1551) and took the collision with it. The
 * rule it was there to record still holds and is worth keeping: TanStack ranks
 * static above dynamic regardless of declaration order, so the day someone adds
 * `/loft/drafts`, ordering is belt and braces rather than the thing keeping it
 * working.
 */
const correspondenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loft/$handle',
  component: function CorrespondenceRoute() {
    const { handle } = correspondenceRoute.useParams();
    return <CorrespondenceScreen handle={handle} />;
  },
});

/*
 * One open letter and the comments on it (`LetterScreen`).
 *
 * `$id` RATHER THAN A SLUG, unlike a room, because a letter has no name — and unlike
 * a correspondence, which is addressed by the PAIR (`/loft/$handle`) precisely
 * because a private letter is nobody's to link to. An open letter is public, in a
 * room, and answerable by anyone who can see it, so a link to one is the ordinary
 * case rather than a leak.
 *
 * ONE ADDRESS PER CONVERSATION. A comment's id is a valid `$id` and resolves to the
 * letter it answers, which is the server's rule (`GET /posts/:id`) — so this route
 * cannot be rooted at a fragment, and the cards that link here do not have to know
 * which of the two they are holding.
 *
 * THIS IS THE COLD-ENTRY RENDERING. A live Feed, room or profile uses its nested
 * masked route above so that same address rises as a sheet over the list. On a
 * reload the mask deliberately falls away and this route owns the page.
 *
 * NOT `/spaces/$slug/$id`, though every letter is in a room: a letter would then have
 * as many addresses as there are ways to say where it was written, and a card would
 * need the room's slug to link to the letter it is drawing.
 */
const letterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/letters/$id',
  component: function LetterRoute() {
    const { id } = letterRoute.useParams();
    return <LetterScreen id={id} />;
  },
});

const routeTree = rootRoute.addChildren([
  feedRoute.addChildren([feedIndexRoute, feedCommentsRoute]),
  routeCanaryRoute.addChildren([routeCanaryCommentsRoute]),
  composeRoute,
  letterRoute,
  loftRoute,
  correspondenceRoute,
  spaceRoute.addChildren([spaceCommentsRoute]),
  doveRoute,
  youRoute.addChildren([youCommentsRoute]),
  peopleRoute,
  editProfileRoute,
  notificationsRoute,
  profileRoute.addChildren([profileCommentsRoute]),
  joinRoute,
]);

/*
 * GOING BACK PUTS YOU BACK, AT THE LINE YOU LEFT OFF ON (docs/5 § D260809-0359).
 *
 * The feed is a scroll somebody is a long way down. Opening a letter and coming
 * back used to land at the top of it, which is the whole of the reading lost —
 * and the browser's own restoration cannot help, because THE SCROLLER IS NOT THE
 * WINDOW: it is the `<main>` inside `AppShell`, so that the tab bar stays put
 * while the list moves. `history.scrollRestoration` only ever knew about the
 * window, which in this app never scrolls at all.
 *
 * So the router does it. It watches whatever actually scrolled, files the
 * position against the history entry, and puts it back when that entry returns —
 * one mechanism for every screen, including the ones added after this line.
 *
 * `scrollToTopSelectors` IS NOT OPTIONAL HERE, AND IT IS THE HALF THAT IS EASY TO
 * MISS. Left out, an element scroller CARRIES ITS POSITION FORWARD onto the next
 * location — the default is written for a scroller that persists across a
 * navigation, which is exactly the shape of ours — so opening a letter from
 * halfway down the feed would open the letter halfway down. Naming the scroller
 * here says the opposite: a new place starts at its top, and only a place you are
 * RETURNING to is restored.
 */
const APP_SCROLLER = `[data-scroll-restoration-id="${SCROLLER_ID}"]`;

export const router = createRouter({
  routeTree,
  /* Hover/focus and touch-start fetch the small route chunk before navigation.
     React Query remains the owner of server-data freshness. */
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  scrollToTopSelectors: [APP_SCROLLER],
});

/* The note the back button reads to know whether it is unwinding or replacing.
   Started here rather than in `main.tsx` because it belongs to the router and
   takes no part in rendering — and because a component would start it after the
   first location had already resolved unrecorded. */
trackHistoryTrail(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
